import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ContaAzulRepository } from '@/services/conta-azul/conta-azul.repository';
import type { ContaAzulEntryDataInput, CreateSupplierInput } from '@/services/conta-azul/conta-azul.types';

const repository = new ContaAzulRepository();

// === Query Keys ===

export const contaAzulKeys = {
  all: ['conta-azul'] as const,
  integration: (customerId: string) => [...contaAzulKeys.all, 'integration', customerId] as const,
  costCenters: (customerId: string) => [...contaAzulKeys.all, 'cost-centers', customerId] as const,
  financialAccounts: (customerId: string) => [...contaAzulKeys.all, 'financial-accounts', customerId] as const,
  expenseCategories: (customerId: string) => [...contaAzulKeys.all, 'expense-categories', customerId] as const,
  suppliers: (customerId: string) => [...contaAzulKeys.all, 'suppliers', customerId] as const,
  entryData: (entryId: string) => [...contaAzulKeys.all, 'entry-data', entryId] as const,
};

// === Queries ===

export const useContaAzulIntegration = (customerId: string, enabled = true) => {
  return useQuery({
    queryKey: contaAzulKeys.integration(customerId),
    queryFn: async () => {
      try {
        return await repository.getIntegration(customerId);
      } catch (error: unknown) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: enabled && !!customerId,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
};

export const useCostCenters = (customerId: string, enabled = true) => {
  return useQuery({
    queryKey: contaAzulKeys.costCenters(customerId),
    queryFn: () => repository.getCostCenters(customerId),
    enabled: enabled && !!customerId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useFinancialAccounts = (customerId: string, enabled = true) => {
  return useQuery({
    queryKey: contaAzulKeys.financialAccounts(customerId),
    queryFn: () => repository.getFinancialAccounts(customerId),
    enabled: enabled && !!customerId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useExpenseCategories = (customerId: string, enabled = true) => {
  return useQuery({
    queryKey: contaAzulKeys.expenseCategories(customerId),
    queryFn: () => repository.getExpenseCategories(customerId),
    enabled: enabled && !!customerId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useSuppliers = (customerId: string, enabled = true) => {
  return useQuery({
    queryKey: contaAzulKeys.suppliers(customerId),
    queryFn: () => repository.getSuppliers(customerId),
    enabled: enabled && !!customerId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useContaAzulEntryData = (entryId: string, enabled = true) => {
  return useQuery({
    queryKey: contaAzulKeys.entryData(entryId),
    queryFn: () => repository.getEntryData(entryId),
    enabled: enabled && !!entryId,
    retry: false,
  });
};

// === Mutations ===

export const useDeactivateIntegration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (integrationId: string) => repository.deactivate(integrationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contaAzulKeys.all });
    },
  });
};

export const useRefreshToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (customerId: string) => repository.refreshToken(customerId),
    onSuccess: (_data, customerId) => {
      queryClient.invalidateQueries({ queryKey: contaAzulKeys.integration(customerId) });
    },
  });
};

export const useUpsertEntryData = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entryId, data }: { entryId: string; data: ContaAzulEntryDataInput }) =>
      repository.upsertEntryData(entryId, data),
    onSuccess: (_data, { entryId }) => {
      queryClient.invalidateQueries({ queryKey: contaAzulKeys.entryData(entryId) });
    },
  });
};

export const useCreateSupplier = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateSupplierInput) => repository.createSupplier(input),
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: contaAzulKeys.suppliers(input.customerId) });
    },
  });
};

export const useSyncEntry = () => {
  return useMutation({
    mutationFn: ({ entryId, customerId }: { entryId: string; customerId: string }) =>
      repository.syncEntry(entryId, customerId),
  });
};

